import React from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

/*
 * ⛔ `/view`, NOT the root barrel. The root re-exports `BuildingEntityIcon` and `useBuildingImage`,
 * which reach for a query client; the disc beside a vendor's name is purely presentational.
 */
import { EntityIcon } from '@hqo/entity-icon/view';

import { AddOnCharge } from 'store/api-client/add-on-payment-types';
import { formatCurrency } from 'utils/formatCurrency';
import { initialsOf } from 'utils/initials';
import { formatDate } from 'utils/formatDate';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { useLocale } from 'hooks/use-locale.hook';

import { AddOnStatus } from 'components/ordered-add-ons/add-on-status';
import { StatusDot, StatusLabel, StatusPillContainer } from 'components/booking-tile/redesigned/styles';

import { useFlags } from 'launchdarkly-react-client-sdk';
import { AddOnProductIcon } from 'components/add-on-product-icon';
import { AwaitingPriceNote, PaidNote, PartyIdentity, PartyStatus, RefundNote, SummaryCard } from './styles';
import { AddOnsLine, TotalLine } from './summary-lines';
import { WITHHELD_AMOUNT } from './estimate-summary';

export interface OrderedAddOnLine {
  key: string;
  name: string;
  quantity: number;
  price: number;
  currency?: string | null;
  /*
   * RES-1317. Whether `price` is a figure someone set. Absent on the step's frozen order, which has
   * the catalog's own pricing type and needs no inference; present on the booking card, where a zero
   * is otherwise unreadable because helix stores one for a quote-required product and for a free one
   * alike.
   */
  priceKnown?: boolean;
  /* VIS-2193. The product image, where the source carries one (the step's frozen order does). */
  imageUrl?: string;
  /* Its catalog alt text, where the source carries one. Empty otherwise: the name is beside it. */
  imageAltText?: string;
}

/*
 * The same three things the picker says, from the only field the stored line carries. A zero means
 * two different things here: rendering every zero as "Price on request" would make free products
 * unreadable, and reading the amount alone would price a pending quote at nothing. A pure function
 * taking its intl, like `addOnSubtitle`, so it is testable without rendering.
 */
export const linePriceLabel = (line: OrderedAddOnLine, intl: IntlShape, locale: string): string => {
  if (line.priceKnown === false) {
    return intl.formatMessage({ id: 'add_on_price_on_request' });
  }

  if (line.priceKnown === true && line.price === 0) {
    return intl.formatMessage({ id: 'add_on_price_free' });
  }

  return line.currency
    ? formatCurrency(line.price * line.quantity, line.currency, locale, true, false)
    : WITHHELD_AMOUNT;
};

interface OrderedAddOnsListProps {
  lines: Array<OrderedAddOnLine>;
  partyName?: string;
  /*
   * The figure under the lines, and the STRING THAT NAMES IT. The label travels with the amount
   * because the two cannot be chosen apart: "Estimated total" is only honest over our own sum of the
   * lines, while billing's `totalCents` is a real figure that nothing here estimated. See
   * `addOnReceipt`, which is the one place that decision is made.
   */
  total?: { labelId: string; amount: number; currency: string | null };
  /*
   * RES-1316 — tax, in minor units, and ONLY ever an estimate on an unpriced order. work-orders
   * nulls its estimate the moment `totalCents` is set, and a priced order's real tax cannot be
   * recovered from the tenant read at all — see `addOnReceipt`. So this is absent on every order
   * that was actually charged, which is why it is optional rather than a line that renders zero.
   */
  tax?: { amountCents: number; currency: string } | null;
  /*
   * RES-1316 — minor units already returned. In the charge's own currency, since nothing refunds in
   * a currency it did not take.
   */
  refundedCents?: number | null;
  /*
   * RES-1316 — nothing has priced this order yet, so there is no total and the card says so rather
   * than leaving a gap where a figure belongs. See `addOnReceipt` for why the lines cannot simply
   * be added up in that state.
   */
  isAwaitingPrice?: boolean;
  /*
   * RES-1336 — what billing actually took, once the work order says it was taken.
   *
   * ⭐ IT OUTRANKS `total`, and it can state a figure where `total` cannot. An estimate is ours and a
   * charge is the vendor's; the two can legitimately differ because the vendor prices the job, so
   * showing our estimate beside the word "paid" would state a charge that was never made. And a
   * caller with no stateable estimate — stored rows carry neither a currency nor a pricing type, so a
   * quote-required line is indistinguishable from a free one — can still state a settled charge
   * honestly, because that figure arrives from billing with its own currency.
   */
  charge?: AddOnCharge | null;
  /* RES-1316 — the vendor's logo, where helix resolved one. The initials disc is the fallback. */
  partyLogoUrl?: string | null;
  /** Where the order stands. Null on the confirmation screen, which renders before any payment read. */
  status?: AddOnStatus | null;
  testIdPrefix: string;
  className?: string;
  /*
   * VIS-2193. Draw a thumbnail on each line, the product image or the `Package` box (behind
   * `show-product-images`), the same tile the add-on step and dialog draw.
   */
  withProductImages?: boolean;
}

/*
 * The ordered add-ons under their one party header, shared by the add-on step's confirmation screen
 * and the tenant's booking detail so the two cannot drift. `price` is the unit price, so a line
 * figure is price x quantity, and an amount is withheld rather than invented without a currency.
 *
 * ⛔ THE MONEY LINE LIVES HERE, NOT AT THE CALL SITES. RES-1316 extracted this component out of the
 * confirmation screen while RES-1336 was adding the paid-versus-estimated swap to the very block
 * being extracted. Resolving that by leaving the total at each call site would have put two copies of
 * "is this paid, and what do we call the figure" on two screens that must agree — which is the exact
 * drift this component exists to prevent.
 */
export const OrderedAddOnsList = ({
  lines,
  partyName,
  total,
  tax: taxEstimate,
  refundedCents,
  isAwaitingPrice = false,
  charge,
  partyLogoUrl,
  status,
  testIdPrefix,
  className,
  withProductImages = false,
}: OrderedAddOnsListProps): JSX.Element => {
  const intl = useIntl();
  /* VIS-2193. Thumbnails only where the lines carry images AND the flag is on. */
  const { showProductImages } = useFlags();
  const showThumbnails = withProductImages && !!showProductImages;
  const locale = useLocale();
  const timeZone = useSelector(selectBuildingTimeZone);

  /* Withheld for the same reason the picker withholds it: a total across currencies states nothing. */
  const estimate = total
    ? {
        labelId: total.labelId,
        amount: total.currency ? formatCurrency(total.amount, total.currency, locale, true, false) : WITHHELD_AMOUNT,
      }
    : null;

  /*
   * ⚠️ Withheld with the total it belongs to, never on its own: a tax figure above a dash reads as
   * the only thing we know about the money, when in fact it is the only thing we could not state.
   */
  /*
   * ⚠️ In the WORK ORDER's currency, which arrives with the figure. Never the booking's: the vendor
   * bills in whatever it bills in, and labelling its tax with the room's currency misstates it
   * whenever the two differ. Withheld entirely when no currency came with it.
   */
  const tax = taxEstimate ? formatCurrency(taxEstimate.amountCents, taxEstimate.currency, locale, true, false) : null;

  /* ⚠️ Both minor units: `formatCurrency` divides by 100 itself, and `totalCents` arrives that way. */
  const settled = charge
    ? {
        labelId: 'add_on_total_paid',
        amount: formatCurrency(charge.totalCents, charge.currency, locale, true, false),
      }
    : null;

  const totalRow = settled ?? estimate;

  /*
   * ⚠️ The date alone when the gateway gave no card — `cardLastFour` is nullable and some gateways
   * never populate it — and nothing at all when neither arrived, rather than a sentence with a hole
   * in it. Written as whole sentences per case so each locale can order them its own way.
   *
   * ⚠️ The repo's own `formatDate`, not `intl.formatDate` with hand-picked options: the confirmation
   * screen's when-line uses it through `bookingWhen`, and two different date shapes on one card is
   * the kind of thing nobody notices in English and everybody notices in de-DE. Same building
   * timezone as the booking, so "paid on" and "booked for" cannot disagree about the day.
   */
  const paidOn = charge?.paidAt ? formatDate(charge.paidAt, locale as string, timeZone).formattedFullDate : null;

  const paidNote = ((): { id: string; values?: Record<string, string> } | null => {
    if (!charge) {
      return null;
    }

    if (paidOn && charge.cardLastFour) {
      return { id: 'add_on_paid_with_card', values: { date: paidOn, lastFour: charge.cardLastFour } };
    }

    if (paidOn) {
      return { id: 'add_on_paid_on', values: { date: paidOn } };
    }

    return charge.cardLastFour ? { id: 'add_on_paid_card_only', values: { lastFour: charge.cardLastFour } } : null;
  })();

  /*
   * ⚠️ In the CHARGE's currency, so it is only stateable alongside one. A refund without the charge
   * it reverses is an amount with nothing to measure it against.
   */
  const refunded = refundedCents && charge ? formatCurrency(refundedCents, charge.currency, locale, true, false) : null;

  return (
    <>
      {/*
        ⛔ THE LOGO STANDS ON ITS OWN, BECAUSE HELIX SAYS IT CAN. It emits a party when EITHER half
        resolved — `(company.name || company.logo_url)` — and keeps the two independent deliberately,
        with a test for a company that has a logo and no name. Hanging the whole row off the name
        threw that mark away in the one case where it is all the tenant has to go on.
      */}
      {(partyName || partyLogoUrl || status) && (
        <PartyIdentity data-testid={`${testIdPrefix}-party`}>
          {/*
            RES-1367: the design system's own entity disc, at `md` — 40px. It went to `lg` (56px)
            first and came back: the party name is the only other thing on that row, and at 56 the
            disc outweighed it. The scale is the package's rather than a hand-rolled box, so the
            initials move with the tile — `text-xs` is what `md` renders its own text at.

            ⭐ `fallback`, NOT `text`. `text` is painted OVER the image under a scrim, so a vendor
            with both would get their initials burned across their own mark. `fallback` renders in
            exactly the branch where the image is not showing — absent, still loading, or failed —
            which is the one branch the retired `onError` flag existed to detect. Two hand-kept
            pieces of state (set on error, cleared when the url changes) collapse into a prop,
            and with them the bug that state was there to prevent: one vendor's broken image can no
            longer suppress the next one's, because nothing remembers the failure.

            ⚠️ Decorative ONLY WHERE THE NAME IS BESIDE IT, so `alt=""` rather than the vendor's name
            a screen reader would then hear twice.

            ⭐ AND NAMELESS IS A REAL STATE, not a defensive branch: the name is the first ordered row
            carrying a `catalog_company_name`, while the logo comes from the party record, so rows
            that predate helix denormalising the name leave a disc with no text anywhere near it.
            Decorative then means the whole identity block reads as empty, and a tenant on a screen
            reader is told nothing about who is providing their add-ons. The generic label is the
            honest alternative -- it says a provider is attributed without inventing which one.

            ⚠️ STILL `resolveHelixUploadUrl`'s OUTPUT, not the stored value. `EntityIcon` resolves an
            `uploads/…` path itself, but it knows nothing of helix's OTHER form — a root-relative
            `/…` served from the API host, which needs `apiUrl` to resolve. One place understands
            every shape helix stores; this hands it a url that is already a url.
          */}
          {(partyName || partyLogoUrl) && (
            <EntityIcon
              imageUrl={partyLogoUrl}
              fallback={partyName ? <span className="text-xs font-semibold">{initialsOf(partyName)}</span> : undefined}
              size="md"
              shape="circle"
              alt={partyName ? '' : intl.formatMessage({ id: 'add_on_party_logo_alt' })}
            />
          )}
          {partyName && <strong>{partyName}</strong>}
          {/* The same pill the booking's own title uses, so one booking has one way of saying where it stands. */}
          {!!status && (
            <PartyStatus data-testid={`${testIdPrefix}-status`}>
              <StatusPillContainer variant={status.variant}>
                <StatusDot variant={status.variant} />
                <StatusLabel variant={status.variant}>{intl.formatMessage({ id: status.labelId })}</StatusLabel>
              </StatusPillContainer>
            </PartyStatus>
          )}
        </PartyIdentity>
      )}

      <SummaryCard className={className} data-testid={`${testIdPrefix}-summary`}>
        {lines.map(line => (
          <AddOnsLine
            key={line.key}
            leading={
              showThumbnails ? (
                <AddOnProductIcon imageUrl={line.imageUrl} imageAltText={line.imageAltText} />
              ) : undefined
            }
            /* A bare name at quantity one: "1 x Coffee service" reads like a defect. */
            title={line.quantity > 1 ? `${line.quantity} × ${line.name}` : line.name}
            price={linePriceLabel(line, intl, locale)}
          />
        ))}
        {/*
          Above the total's rule, as a line of the receipt rather than a footnote to it: it is part
          of the arithmetic the total sums, and the only reason it is conditional is that
          work-orders has no figure to give once an order is priced.
        */}
        {/*
          ⚠️ No test id, and not an oversight: RCL's `AddOnItem` destructures `{ title, price,
          className, quantity }` and spreads nothing, so any `data-testid` handed to it is dropped
          silently. The row is asserted by its text, which is what a tenant reads anyway.
        */}
        {tax !== null && <AddOnsLine title={intl.formatMessage({ id: 'add_on_tax_estimated' })} price={tax} />}
        {totalRow && <TotalLine title={intl.formatMessage({ id: totalRow.labelId })} price={totalRow.amount} />}
        {/*
          Inside the summary card, under the total it describes — a receipt line, not an announcement.
          On the confirmation screen the sheet has already said the payment succeeded; this is the record.
        */}
        {/*
          ⛔ Alongside an estimated total when work-orders could compute one, and in place of any
            figure when it could not. This used to be strictly either/or because there was no total
            anyone could state: the tax estimate cannot be added to the lines, and the lines
            understate a quote-required row. RBS#196 exposes `estimatedTotalCents`, computed by
            @hqo/billing-tax itself, so the note now qualifies a figure rather than replacing it. A
            line the vendor has still to quote leaves the card explaining the absence, as before.
        */}
        {isAwaitingPrice && (
          <AwaitingPriceNote data-testid={`${testIdPrefix}-awaiting-price`}>
            {intl.formatMessage({ id: 'add_on_total_after_pricing' })}
          </AwaitingPriceNote>
        )}
        {paidNote && (
          <PaidNote data-testid={`${testIdPrefix}-paid`}>
            {intl.formatMessage({ id: paidNote.id }, paidNote.values)}
          </PaidNote>
        )}
        {/* Under the paid line it qualifies, and never instead of it — both movements happened. */}
        {refunded && (
          <RefundNote data-testid={`${testIdPrefix}-refunded`}>
            {intl.formatMessage({ id: 'add_on_refunded' }, { amount: refunded })}
          </RefundNote>
        )}
      </SummaryCard>
    </>
  );
};
